import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Copy, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_OPTIONS = [1, 3, 7, 14, 30];
const STATE_CLS = { active: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', expired: 'bg-gray-100 text-gray-600 hover:bg-gray-100', revoked: 'bg-red-100 text-red-700 hover:bg-red-100' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const NewPassBanner = ({ created, onDismiss }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="testing-pass-created">
    <p className="text-sm font-semibold text-amber-900">Testing pass for {created.pass.email} — copy it now, it won't be shown again.</p>
    <div className="flex items-center gap-2 mt-2">
      <code className="flex-1 font-mono text-sm bg-white border rounded px-3 py-2 break-all" data-testid="testing-pass-code">{created.code}</code>
      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(created.code); toast.success('Copied'); }}
        data-testid="testing-pass-copy-btn"><Copy className="w-4 h-4" /></Button>
    </div>
    <p className="text-xs text-amber-800 mt-2">
      Sign in with the account's password, then on the code screen choose "Use a testing pass instead". Scripts can send it as
      <code className="mx-1">code</code> to <code>POST /api/auth/admin-2fa/verify</code>.
    </p>
    <button type="button" onClick={onDismiss} className="text-xs text-amber-900 underline mt-2" data-testid="testing-pass-dismiss">I've saved it</button>
  </div>
);

const PassRow = ({ p, onRevoke }) => (
  <tr className="border-b last:border-0" data-testid={`testing-pass-row-${p.pass_id}`}>
    <td className="py-2.5 pr-4 text-sm"><p className="font-medium">{p.email}</p><p className="text-xs text-gray-500">{p.label || '—'} · …{p.hint}</p></td>
    <td className="py-2.5 pr-4"><Badge className={STATE_CLS[p.state]} data-testid={`testing-pass-state-${p.pass_id}`}>{p.state}</Badge></td>
    <td className="py-2.5 pr-4 text-xs text-gray-600">{fmt(p.expires_at)}</td>
    <td className="py-2.5 pr-4 text-xs text-gray-600">{p.use_count} use(s)<br />{p.last_used_at ? `last ${fmt(p.last_used_at)}` : 'never used'}</td>
    <td className="py-2.5 pr-4 text-xs text-gray-600">{p.created_by}</td>
    <td className="py-2.5 text-right">
      {p.state === 'active' && (
        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => onRevoke(p)}
          data-testid={`testing-pass-revoke-${p.pass_id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Revoke</Button>
      )}
    </td>
  </tr>
);

const TestingPassesCard = () => {
  const [passes, setPasses] = useState(null);
  const [form, setForm] = useState({ email: '', days: '7', label: '' });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/testing-passes`, { withCredentials: true });
      setPasses(res.data.passes);
    } catch {
      setPasses([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/testing-passes`, { ...form, days: Number(form.days) }, { withCredentials: true });
      setCreated(res.data);
      setForm({ email: '', days: '7', label: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create pass');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (p) => {
    try {
      const res = await axios.delete(`${API}/admin/testing-passes/${p.pass_id}`, { withCredentials: true });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  return (
    <Card data-testid="testing-passes-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> 2-step testing passes</CardTitle>
        <CardDescription>
          Let one admin account skip the emailed sign-in code — for QA on the live site or automated tests. Every use is logged and all admins are alerted when a pass is created.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {created && <NewPassBanner created={created} onDismiss={() => setCreated(null)} />}
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-3 mb-5">
          <Input required type="email" placeholder="Admin email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="testing-pass-email" />
          <Select value={form.days} onValueChange={(v) => setForm({ ...form, days: v })}>
            <SelectTrigger data-testid="testing-pass-days"><SelectValue /></SelectTrigger>
            <SelectContent>{DAY_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Label (e.g. Playwright CI)" maxLength={80} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid="testing-pass-label" />
          <Button type="submit" disabled={busy} data-testid="testing-pass-create-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create pass'}
          </Button>
        </form>
        {passes === null ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : passes.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="testing-passes-empty">No testing passes yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Account</th><th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Expires</th><th className="py-2 pr-4 font-medium">Usage</th>
                  <th className="py-2 pr-4 font-medium">Created by</th><th />
                </tr>
              </thead>
              <tbody>{passes.map((p) => <PassRow key={p.pass_id} p={p} onRevoke={revoke} />)}</tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestingPassesCard;
